<template>
  <div class="report-page">
    <!-- Top Bar -->
    <header class="bol-header">
      <div class="header-left">
        <span class="header-logo">📊</span>
        <h1 class="header-title">Reporte Boletería</h1>
      </div>
      <div class="header-right">
        <router-link to="/boleteria" class="nav-link">🎫 Boletería</router-link>
        <router-link to="/admin/dashboard" class="nav-link">📈 Analytics</router-link>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <div v-if="!token" class="auth-redirect">
      <p>Sesión no iniciada</p>
      <router-link :to="'/admin?redirect=/boleteria/report'" class="login-link">Iniciar sesión →</router-link>
    </div>

    <main v-else class="report-content">
      <!-- Función Selector -->
      <div class="filter-bar">
        <select v-model="selectedFuncion" @change="fetchReport" class="form-select">
          <option :value="null">Todas las funciones</option>
          <option v-for="f in funciones" :key="f.id" :value="f.id">
            {{ f.name }} — {{ f.date }}
          </option>
        </select>
        <button @click="fetchReport" class="refresh-btn">🔄</button>
      </div>

      <div v-if="loading" class="loading-text">Cargando datos...</div>

      <div v-else-if="report">
        <!-- KPI Cards -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <span class="kpi-value">{{ report.summary.total_sold }}</span>
            <span class="kpi-label">Vendidos</span>
          </div>
          <div class="kpi-card kpi-green">
            <span class="kpi-value">{{ report.summary.total_used }}</span>
            <span class="kpi-label">Ingresados</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-value">{{ report.summary.total_valid }}</span>
            <span class="kpi-label">Pendientes</span>
          </div>
          <div class="kpi-card kpi-red">
            <span class="kpi-value">{{ report.summary.total_cancelled }}</span>
            <span class="kpi-label">Cancelados</span>
          </div>
          <div class="kpi-card kpi-gold">
            <span class="kpi-value">${{ report.summary.total_revenue }}</span>
            <span class="kpi-label">Recaudación</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-value">{{ report.summary.total_with_contact }}</span>
            <span class="kpi-label">Con datos</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-value">{{ attendanceRate }}%</span>
            <span class="kpi-label">Asistencia</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-value">{{ contactRate }}%</span>
            <span class="kpi-label">Datos capturados</span>
          </div>
        </div>

        <!-- Payment Method Breakdown -->
        <div class="section-card">
          <h3 class="section-title">💳 Por método de pago</h3>
          <div class="breakdown-list">
            <div v-for="(data, method) in report.by_payment_method" :key="method" class="breakdown-item">
              <div class="breakdown-left">
                <span class="breakdown-icon">{{ paymentIcon(method) }}</span>
                <span class="breakdown-name">{{ paymentLabel(method) }}</span>
              </div>
              <div class="breakdown-right">
                <span class="breakdown-count">{{ data.count }} tickets</span>
                <span class="breakdown-revenue">${{ data.revenue }} UYU</span>
                <span class="breakdown-used">{{ data.used }} ingresados</span>
              </div>
              <div class="breakdown-bar">
                <div
                  class="breakdown-bar-fill"
                  :style="{ width: barWidth(data.count) + '%' }"
                ></div>
              </div>
            </div>
            <div v-if="Object.keys(report.by_payment_method).length === 0" class="empty-text">
              No hay datos
            </div>
          </div>
        </div>

        <!-- Per Función Breakdown (when showing all) -->
        <div v-if="report.by_funcion && report.by_funcion.length > 0" class="section-card">
          <h3 class="section-title">🎭 Por función</h3>
          <div class="funcion-list">
            <div v-for="f in report.by_funcion" :key="f.funcion_id" class="funcion-item">
              <div class="funcion-name">{{ f.funcion_name }}</div>
              <div class="funcion-stats">
                <span class="funcion-stat">🎫 {{ f.sold }}</span>
                <span class="funcion-stat">✅ {{ f.used }}</span>
                <span class="funcion-stat">❌ {{ f.cancelled }}</span>
                <span class="funcion-stat funcion-revenue">${{ f.revenue }}</span>
              </div>
              <div class="breakdown-bar">
                <div
                  class="breakdown-bar-fill bar-used"
                  :style="{ width: (f.sold > 0 ? (f.used / f.sold) * 100 : 0) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contacts List -->
        <div class="section-card">
          <h3 class="section-title" @click="showContacts = !showContacts" style="cursor:pointer">
            👥 Contactos capturados {{ showContacts ? '▼' : '▶' }}
            <span class="contact-count">({{ contacts.length }})</span>
          </h3>
          <div v-if="showContacts">
            <div v-if="contacts.length === 0" class="empty-text">No hay contactos vinculados</div>
            <div v-else class="contacts-list">
              <div class="contacts-header">
                <span class="col-name">Nombre</span>
                <span class="col-email">Email</span>
                <span class="col-phone">Teléfono</span>
                <span class="col-source">Origen</span>
                <span class="col-news">📰</span>
                <span class="col-member">🏛️</span>
              </div>
              <div v-for="c in contacts" :key="c.id" class="contact-row">
                <span class="col-name">{{ c.first_name }} {{ c.last_name }}</span>
                <span class="col-email">{{ c.email || '—' }}</span>
                <span class="col-phone">{{ c.phone || '—' }}</span>
                <span class="col-source">{{ sourceLabel(c.source) }}</span>
                <span class="col-news">{{ c.wants_news ? '✅' : '—' }}</span>
                <span class="col-member">{{ c.wants_membership ? '✅' : '—' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
const API_BASE = '/api'

export default {
  name: 'BoleteriaReport',
  data() {
    return {
      token: sessionStorage.getItem('admin_token'),
      funciones: [],
      selectedFuncion: null,
      report: null,
      contacts: [],
      loading: false,
      showContacts: false,
    }
  },
  computed: {
    attendanceRate() {
      if (!this.report || this.report.summary.total_sold === 0) return 0
      const active = this.report.summary.total_sold - this.report.summary.total_cancelled
      if (active === 0) return 0
      return Math.round((this.report.summary.total_used / active) * 100)
    },
    contactRate() {
      if (!this.report || this.report.summary.total_sold === 0) return 0
      return Math.round((this.report.summary.total_with_contact / this.report.summary.total_sold) * 100)
    },
    maxCount() {
      if (!this.report) return 1
      const counts = Object.values(this.report.by_payment_method).map(d => d.count)
      return Math.max(...counts, 1)
    },
  },
  async mounted() {
    if (!this.token) return
    await this.fetchFunciones()
    await this.fetchReport()
  },
  methods: {
    async fetchFunciones() {
      try {
        const res = await fetch(`${API_BASE}/tickets/funciones?all=1`, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.status === 401) { this.logout(); return }
        if (res.ok) {
          const data = await res.json()
          this.funciones = data.funciones
        }
      } catch { /* silent */ }
    },

    async fetchReport() {
      this.loading = true
      try {
        let url = `${API_BASE}/tickets/report`
        if (this.selectedFuncion) {
          url += `?funcion_id=${this.selectedFuncion}`
        }
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.status === 401) { this.logout(); return }
        if (res.ok) {
          this.report = await res.json()
          // Extract unique contacts from history
          await this.fetchContacts()
        }
      } catch { /* silent */ }
      this.loading = false
    },

    async fetchContacts() {
      try {
        let url = `${API_BASE}/tickets/history?limit=200`
        if (this.selectedFuncion) {
          url += `&funcion_id=${this.selectedFuncion}`
        }
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.ok) {
          const data = await res.json()
          // Extract unique contacts
          const contactMap = {}
          data.tickets.forEach(t => {
            if (t.contact && t.contact.id) {
              contactMap[t.contact.id] = t.contact
            }
          })
          this.contacts = Object.values(contactMap)
        }
      } catch { /* silent */ }
    },

    barWidth(count) {
      return (count / this.maxCount) * 100
    },

    paymentLabel(method) {
      return { cash: 'Efectivo', transfer: 'Transferencia', redtickets: 'RedTickets', invitation: 'Invitación', combo: 'Combo', '2x1': '2 por 1' }[method] || method
    },

    paymentIcon(method) {
      return { cash: '💵', transfer: '🏦', redtickets: '🎟️', invitation: '🎁', combo: '📦', '2x1': '🤝' }[method] || '💳'
    },

    sourceLabel(source) {
      return { ticket_sale: 'Venta', ticket_link: 'Vinculado', redtickets: 'RedTickets', website: 'Web', manual: 'Manual' }[source] || source
    },

    logout() {
      sessionStorage.removeItem('admin_token')
      this.$router.push('/admin?redirect=/boleteria/report')
    }
  }
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background: #0a0a0a;
  color: #e0e0e0;
}

/* Header */
.bol-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 24px; background: #111; border-bottom: 1px solid #222;
  flex-wrap: wrap; gap: 10px;
}
.header-left { display: flex; align-items: center; gap: 10px; }
.header-logo { font-size: 28px; }
.header-title { font-size: 20px; color: #c9a227; margin: 0; }
.header-right { display: flex; align-items: center; gap: 10px; }
.nav-link {
  color: #888; text-decoration: none; font-size: 13px;
  padding: 6px 10px; border-radius: 6px; transition: all 0.2s;
}
.nav-link:hover { color: #c9a227; background: #1a1a1a; }
.logout-btn {
  background: #333; color: #aaa; border: none; padding: 6px 14px;
  border-radius: 6px; cursor: pointer; font-size: 13px;
}
.auth-redirect { text-align: center; padding: 100px 20px; color: #666; }
.login-link { display: inline-block; margin-top: 15px; color: #c9a227; text-decoration: none; }

/* Content */
.report-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Filter */
.filter-bar {
  display: flex;
  gap: 10px;
  align-items: center;
}
.form-select {
  flex: 1;
  padding: 12px 14px;
  background: #111;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 15px;
  outline: none;
}
.form-select:focus { border-color: #c9a227; }
.form-select option { background: #111; }
.refresh-btn {
  background: #222;
  border: 1px solid #333;
  color: #c9a227;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
}
.refresh-btn:hover { background: #333; }

.loading-text {
  text-align: center;
  color: #666;
  padding: 40px;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.kpi-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 10px;
  padding: 16px 12px;
  text-align: center;
}
.kpi-value {
  display: block;
  font-size: 28px;
  font-weight: bold;
  color: #e0e0e0;
}
.kpi-label {
  display: block;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  margin-top: 4px;
}
.kpi-green .kpi-value { color: #2ecc71; }
.kpi-red .kpi-value { color: #e74c3c; }
.kpi-gold .kpi-value { color: #c9a227; }

/* Section Card */
.section-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 24px 20px;
}
.section-title {
  font-size: 16px;
  color: #c9a227;
  margin: 0 0 16px;
}
.contact-count {
  color: #666;
  font-weight: normal;
  font-size: 14px;
}
.empty-text {
  text-align: center;
  color: #555;
  padding: 20px;
  font-size: 14px;
}

/* Payment Breakdown */
.breakdown-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.breakdown-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.breakdown-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
}
.breakdown-icon { font-size: 18px; }
.breakdown-name { font-size: 15px; color: #e0e0e0; font-weight: bold; }
.breakdown-right {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #888;
}
.breakdown-count { color: #e0e0e0; }
.breakdown-revenue { color: #c9a227; }
.breakdown-used { color: #2ecc71; }
.breakdown-bar {
  width: 100%;
  height: 6px;
  background: #1a1a1a;
  border-radius: 3px;
  overflow: hidden;
}
.breakdown-bar-fill {
  height: 100%;
  background: #c9a227;
  border-radius: 3px;
  transition: width 0.3s;
}
.bar-used {
  background: #2ecc71;
}

/* Función Breakdown */
.funcion-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.funcion-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.funcion-name {
  font-size: 15px;
  font-weight: bold;
  color: #e0e0e0;
}
.funcion-stats {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #888;
}
.funcion-stat { }
.funcion-revenue { color: #c9a227; font-weight: bold; }

/* Contacts */
.contacts-list {
  overflow-x: auto;
}
.contacts-header {
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #222;
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  min-width: 700px;
}
.contact-row {
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #111;
  font-size: 13px;
  min-width: 700px;
}
.contact-row:hover { background: #0d0d0d; }
.col-name { flex: 2; min-width: 140px; }
.col-email { flex: 2; min-width: 160px; color: #888; }
.col-phone { flex: 1.5; min-width: 120px; color: #888; }
.col-source { flex: 1; min-width: 80px; color: #666; }
.col-news { flex: 0.3; min-width: 30px; text-align: center; }
.col-member { flex: 0.3; min-width: 30px; text-align: center; }

/* Responsive */
@media (max-width: 600px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .report-content { padding: 12px 8px; }
  .breakdown-item { flex-direction: column; align-items: flex-start; }
  .funcion-stats { flex-wrap: wrap; }
}
</style>